import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PiggyBank, Calendar, CreditCard, DollarSign, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { addEvent } from '../lib/firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import { TransactionCategory } from '../types';
import { normalizeDate, formatDateForInput } from '../utils/dateUtils';

const CATEGORIES: { value: TransactionCategory; label: string }[] = [
  { value: 'food', label: 'Food & Dining' },
  { value: 'groceries', label: 'Groceries' },
  { value: 'dining', label: 'Restaurants' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'shopping', label: 'Shopping' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'housing', label: 'Housing' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'medical', label: 'Medical Expenses' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'education', label: 'Education' },
  { value: 'travel', label: 'Travel' },
  { value: 'personal', label: 'Personal' },
  { value: 'gifts', label: 'Gifts' },
  { value: 'savings', label: 'Savings' },
  { value: 'debt', label: 'Debt' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'pet', label: 'Pet & Vet Expenses' },
  { value: 'other', label: 'Other' }
];

export default function NewUserSetup() {
  const navigate = useNavigate();
  const [setupComplete, setSetupComplete] = React.useState(false);
  const [startingBalance, setStartingBalance] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [formData, setFormData] = React.useState({
    title: '',
    amount: '',
    type: 'bill',
    date: formatDateForInput(new Date()),
    dueDate: '1',
    isDebt: false,
    isSubscription: false,
    isRecurring: false,
    frequency: 'monthly',
    interestRate: '',
    minimumPayment: '',
    category: '',
    isAutopay: false,
    debtBalance: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser) {
      setError('You must be logged in to perform this action');
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const {
        title,
        amount,
        type,
        date,
        isDebt,
        isSubscription,
        isRecurring,
        frequency,
        interestRate,
        minimumPayment,
        dueDate,
        category,
        isAutopay,
        debtBalance
      } = formData;

      if (!title.trim()) {
        setError('Title is required');
        return;
      }

      if (!amount || isNaN(parseFloat(amount))) {
        setError('Please enter a valid amount');
        return;
      }

      if (!date) {
        setError('Date is required');
        return;
      }

      const eventDate = normalizeDate(date);
      const eventAmount = parseFloat(amount);

      // Add to Debt Snowball if it's a debt
      if (isDebt) {
        try {
          const debtData = {
            userId: auth.currentUser.uid,
            name: title,
            balance: parseFloat(debtBalance || amount),
            interestRate: parseFloat(interestRate || '0'),
            minimumPayment: parseFloat(minimumPayment || amount),
            dueDate: parseInt(dueDate),
            createdAt: Timestamp.now()
          };

          const debtRef = await addDoc(collection(db, 'debts'), debtData);

          await addEvent({
            id: crypto.randomUUID(),
            title: `${title} Payment`,
            amount: parseFloat(minimumPayment || amount),
            type: 'bill',
            date: eventDate,
            frequency: isRecurring ? frequency : undefined,
            category: 'debt',
            isAutopay,
            isRecurring,
            debtId: debtRef.id
          });

          setSuccess(`${title} debt added successfully`);
        } catch (error) {
          console.error('Error adding debt:', error);
          setError(error instanceof Error ? error.message : 'Failed to add debt');
          return;
        }
      }
      // Add to Subscriptions if it's a subscription
      else if (isSubscription) {
        try {
          const subscriptionData = {
            userId: auth.currentUser.uid,
            name: title,
            amount: eventAmount,
            dueDate: parseInt(dueDate),
            createdAt: Timestamp.now()
          };

          const subRef = await addDoc(collection(db, 'subscriptions'), subscriptionData);

          await addEvent({
            id: crypto.randomUUID(),
            title: `${title} Subscription`,
            amount: eventAmount,
            type: 'bill',
            date: eventDate,
            frequency: 'monthly',
            category: 'subscription',
            isAutopay,
            isSubscription: true,
            isRecurring: true,
            subscriptionId: subRef.id
          });

          setSuccess(`${title} subscription added successfully`);
        } catch (error) {
          console.error('Error adding subscription:', error);
          setError(error instanceof Error ? error.message : 'Failed to add subscription');
          return;
        }
      }
      // Add regular calendar event
      else {
        try {
          await addEvent({
            id: crypto.randomUUID(),
            title,
            amount: eventAmount,
            type: type as 'bill' | 'payday',
            date: eventDate,
            frequency: isRecurring ? frequency : undefined,
            category: type === 'bill' ? (category as TransactionCategory) : undefined,
            isAutopay,
            isRecurring
          });

          setSuccess(`${title} added successfully`);
        } catch (error) {
          console.error('Error adding event:', error);
          setError(error instanceof Error ? error.message : 'Failed to add event');
          return;
        }
      }

      // Reset form
      setFormData({
        title: '',
        amount: '',
        type: 'bill',
        date: formatDateForInput(new Date()),
        dueDate: '1',
        isDebt: false,
        isSubscription: false,
        isRecurring: false,
        frequency: 'monthly',
        interestRate: '',
        minimumPayment: '',
        category: '',
        isAutopay: false,
        debtBalance: ''
      });

      // Clear messages after delay
      setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 3000);
    } catch (error) {
      console.error('Error adding item:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  };

  const handleFinishSetup = async () => {
    if (startingBalance) {
      try {
        // Add starting balance as an adjustment
        await addEvent({
          id: crypto.randomUUID(),
          title: 'Starting Balance',
          amount: parseFloat(startingBalance),
          type: 'adjustment',
          date: new Date(),
          isPaid: true,
          paidDate: new Date()
        });
      } catch (error) {
        console.error('Error setting starting balance:', error);
        setError('Failed to set starting balance. Please try again.');
        return;
      }
    }
    
    setSetupComplete(true);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Welcome to ClearCash</h1>
          <p className="mt-2 text-lg text-gray-600">Let's set up your financial profile</p>
        </div>

        {/* Starting Balance Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Starting Balance</h3>
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Current Account Balance</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  value={startingBalance}
                  onChange={(e) => setStartingBalance(e.target.value)}
                  placeholder="0.00"
                  className="block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Enter your current account balance to start tracking accurately
          </p>
        </div>

        {/* Setup Instructions */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mb-8">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Getting Started</h3>
              <div className="mt-2 text-sm text-blue-700 space-y-1">
                <p className="font-medium">Use this form to add your:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><span className="text-green-700">Income/Paydays</span> - Select "Income/Payday" as the type</li>
                  <li><span className="text-red-700">Regular Bills</span> - Select "Bill/Expense" and choose a category</li>
                  <li><span className="text-purple-700">Debts</span> - Check "This is a debt" to add to Debt Snowball</li>
                  <li><span className="text-blue-700">Subscriptions</span> - Check "This is a subscription" to track recurring services</li>
                </ul>
                <p className="mt-2">
                  <strong>Pro Tips:</strong>
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Mark bills as "recurring" if they repeat regularly</li>
                  <li>Set up automatic payments by checking "automatic payment"</li>
                  <li>Add your paydays first to see accurate balance projections</li>
                  <li>Don't forget annual bills and subscriptions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-green-50 p-4 rounded-lg text-center">
            <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <h3 className="font-medium">Paydays</h3>
            <p className="text-sm text-gray-600 mt-1">Regular income</p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg text-center">
            <Calendar className="w-8 h-8 text-red-600 mx-auto mb-2" />
            <h3 className="font-medium">Bills</h3>
            <p className="text-sm text-gray-600 mt-1">Regular expenses</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg text-center">
            <PiggyBank className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <h3 className="font-medium">Debts</h3>
            <p className="text-sm text-gray-600 mt-1">Track & pay off</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg text-center">
            <CreditCard className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <h3 className="font-medium">Subscriptions</h3>
            <p className="text-sm text-gray-600 mt-1">Recurring services</p>
          </div>
        </div>

        {!setupComplete && (
          <>
            {/* Form */}
            <div className="bg-white shadow rounded-lg p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {(error || success) && (
                  <div className={`p-4 rounded-md ${
                    error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                  }`}>
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 mr-2" />
                      <span>{error || success}</span>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Amount</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        className="block w-full pl-7 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="bill">Bill/Expense</option>
                      <option value="payday">Income/Payday</option>
                    </select>
                  </div>

                  {formData.type === 'bill' && !formData.isDebt && !formData.isSubscription && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        required
                      >
                        <option value="">Select a category</option>
                        {CATEGORIES.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isDebt"
                        checked={formData.isDebt}
                        onChange={(e) => {
                          const isDebt = e.target.checked;
                          setFormData({
                            ...formData,
                            isDebt,
                            isSubscription: isDebt ? false : formData.isSubscription,
                            category: isDebt ? 'debt' : formData.category
                          });
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isDebt" className="ml-2 block text-sm text-gray-900">
                        This is a debt to be tracked in Debt Snowball
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isSubscription"
                        checked={formData.isSubscription}
                        onChange={(e) => {
                          const isSubscription = e.target.checked;
                          setFormData({
                            ...formData,
                            isSubscription,
                            isDebt: isSubscription ? false : formData.isDebt,
                            category: isSubscription ? 'subscription' : formData.category
                          });
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isSubscription" className="ml-2 block text-sm text-gray-900">
                        This is a subscription
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isRecurring"
                        checked={formData.isRecurring}
                        onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isRecurring" className="ml-2 block text-sm text-gray-900">
                        This is a recurring transaction
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isAutopay"
                        checked={formData.isAutopay}
                        onChange={(e) => setFormData({ ...formData, isAutopay: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isAutopay" className="ml-2 block text-sm text-gray-900">
                        This is set up for automatic payment
                      </label>
                    </div>
                  </div>

                  {formData.isRecurring && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Frequency</label>
                      <select
                        value={formData.frequency}
                        onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                  )}

                  {formData.isDebt && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Current Balance</label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <span className="text-gray-500 sm:text-sm">$</span>
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.debtBalance}
                            onChange={(e) => setFormData({ ...formData, debtBalance: e.target.value })}
                            className="block w-full pl-7 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Interest Rate (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.interestRate}
                          onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Minimum Payment</label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <span className="text-gray-500 sm:text-sm">$</span>
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.minimumPayment}
                            onChange={(e) => setFormData({ ...formData, minimumPayment: e.target.value })}
                            className="block w-full pl-7 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder={formData.amount}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex justify-between space-x-3">
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Skip Setup
                  </button>
                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </button>
                    <button
                      type="button"
                      onClick={handleFinishSetup}
                      className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Finish Setup
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}